/**
 * "How late did this unit paint?" — the three numbers an impression event
 * needs before that question has an answer in PostHog.
 *
 * Until 2026-09-05 `affiliate_impression` and `ad_slot_impression` carried
 * `viewport_ratio` (how much of the unit was on screen when the observer
 * fired) but nothing about WHEN, or WHERE on the page, so "the ad shows up
 * after I've scrolled past it" could be reproduced in a browser and never
 * measured from stored data. These are read off the same
 * `IntersectionObserverEntry` the impression already fires from, so they cost
 * no extra layout work.
 *
 * - `since_nav_ms`: `performance.now()` at fire time — ms since navigation
 *   start, i.e. how long the reader had been on the page before the unit
 *   became viewable. Compare with `web_vitals_ttfb` / `$web_vitals` LCP for
 *   the same page to see whether the unit painted before or after the content.
 * - `slot_top_px`: the unit's document-space top edge. Divided by
 *   `viewport_h` it says how many screens down the slot sits, which is what
 *   turns "late" into "later than a reader reaches it".
 * - `viewport_h`: the viewport height at fire time.
 */
export type SlotTimingProps = {
	since_nav_ms?: number;
	slot_top_px?: number;
	viewport_h?: number;
};

export function slotTimingProps(
	entry: Pick<IntersectionObserverEntry, "boundingClientRect"> | undefined,
): SlotTimingProps {
	if (typeof window === "undefined") return {};
	const out: SlotTimingProps = {};
	if (typeof performance !== "undefined") {
		out.since_nav_ms = Math.round(performance.now());
	}
	if (entry?.boundingClientRect) {
		out.slot_top_px = Math.round(
			entry.boundingClientRect.top + (window.scrollY || 0),
		);
	}
	if (typeof window.innerHeight === "number") {
		out.viewport_h = window.innerHeight;
	}
	return out;
}
